import { action, computed, makeObservable, observable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { ApplicationUuid, GroupData, Task } from '@bringg/types';
import { TimelineItem, TimelineRow } from '@bringg-frontend/bringg-timeline';
import { getTaskStatusClass, taskStyles } from '@bringg-frontend/monitoring-shared';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import RootStore from 'bringg-web/stores/root-store';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import RoutesMonitoringStore from './routes-monitoring-store';
import { timelineProvider } from '../tasks-timeline/timeline-provider';
import { isRouteInvalid } from '../utils/route-utils';

import styles from '../tasks-timeline/tasks-timeline.module.scss';

class RoutesMonitoringTimelineViewStore {
	isRealtimeEtaEnabled: boolean;
	routesMonitoringStore: RoutesMonitoringStore;
	zoom: { start: number; end: number };
	shouldZoom = true;

	constructor() {
		makeObservable(this, {
			zoom: observable,
			rows: computed,
			setZoom: action,
			setShouldZoom: action
		});

		this.routesMonitoringStore = routesMonitoringRootStore.getStore().routesMonitoringStore;
		const { merchantConfigurationsStore } = getRoot<RootStore>().data;
		this.isRealtimeEtaEnabled = !!merchantConfigurationsStore.findApplication(ApplicationUuid.RealtimeEtaApp);
	}

	setZoom = (zoom: { start: number; end: number }) => {
		this.zoom = zoom;
	};

	setShouldZoom = (shouldZoom: boolean) => {
		this.shouldZoom = shouldZoom;
	};

	getRowItems = (tasks: Task[]) => {
		let startEnd = { start: 0, end: 0 };

		const mappedItems: TimelineItem[] = tasks.map((task: Task) => {
			const wayPoint = getActionableWaypoint(task);
			const etos = wayPoint.etos ? wayPoint.etos / 60 : 2;

			const itemClass = getTaskStatusClass(task, this.isRealtimeEtaEnabled);

			const startTime = wayPoint.checkin_time || wayPoint.eta || wayPoint.no_earlier_than;

			const startDate = startTime ? new Date(startTime) : new Date();
			let endTime = startTime ? new Date(startTime) : new Date();

			if (wayPoint.checkout_time) {
				endTime = new Date(wayPoint.checkout_time);
			} else {
				endTime.setMinutes(endTime.getMinutes() + etos);
			}

			startEnd = {
				start: !startEnd.start || startDate.getTime() < startEnd.start ? startDate.getTime() : startEnd.start,
				end: endTime.getTime() > startEnd.end ? endTime.getTime() : startEnd.end
			};

			return {
				id: task.id,
				rowId: task.run_id,
				className: itemClass,
				selectedClassName: taskStyles.selectedTask,
				start: startDate.valueOf(),
				end: endTime.valueOf()
			};
		});

		return { items: mappedItems, startEnd };
	};

	get rows(): TimelineRow[] {
		const startEnd = { start: 0, end: 0 };

		const rows = this.routesMonitoringStore.tableItems.map((item, index) => {
			const { id } = (item as GroupData).data;
			if ((item as GroupData).group_type === 'team') {
				return {
					rowId: item.id,
					order: index,
					items: [],
					className: styles.groupTimelineRow
				};
			} else {
				const route = getRoot<RootStore>().data.runStore.get(Number(id));
				const taskIds = this.routesMonitoringStore.currentTableDefinition.itemsByGroup[
					(item as GroupData).id
				].map(({ id }) => id);

				const routeTasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(taskIds);

				const { items, startEnd: rowStartEnd } = this.getRowItems(routeTasks);

				startEnd.start =
					!startEnd.start || rowStartEnd.start < startEnd.start ? rowStartEnd.start : startEnd.start;
				startEnd.end = rowStartEnd.end > startEnd.end ? rowStartEnd.end : startEnd.end;

				const range = route && timelineProvider.getRangeItem(route);

				range && items.unshift(range);

				return {
					rowId: item.id,
					hasError: isRouteInvalid(+id),
					order: index,
					items
				};
			}
		});

		if (startEnd.start > 0) {
			this.setZoom({ ...timelineProvider.addMarginToZoom(startEnd) });
		}

		return rows;
	}
}

export default RoutesMonitoringTimelineViewStore;
